import { useState, useEffect } from 'react'
import { useTradingStore } from '../hooks/useTradingStore'
import { Settings as SettingsIcon, Save, RefreshCw, AlertTriangle, Database, CheckCircle, XCircle, Loader2, FolderOpen, ShieldCheck } from 'lucide-react'
import StrategySelector from '../components/StrategySelector'
import { CaveatBadge, DataQualityBadge } from '../components/InstrumentSelector'

const API_BASE = import.meta.env.PROD ? '/api' : 'http://localhost:9099/api'

export default function Settings() {
  const {
    strategyParams,
    updateStrategyParams,
    dataCatalogPath,
    dataCatalogExists,
    dataCatalogDefaultPath,
    dataCatalogLoading,
    dataCatalogError,
    backtestInstruments,
    fetchDataCatalogConfig,
    fetchBacktestInstruments,
    setDataCatalogPath,
    scanDataCatalog,
    dataAuditRunning,
    dataAuditProgress,
    dataAuditSummary,
    dataAuditError,
    runDataAudit,
  } = useTradingStore()

  const [localParams, setLocalParams] = useState(strategyParams)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [localCatalogPath, setLocalCatalogPath] = useState(dataCatalogPath)
  const [catalogPathSaved, setCatalogPathSaved] = useState(false)

  useEffect(() => {
    setLocalParams(strategyParams)
  }, [strategyParams])

  useEffect(() => {
    // Fetch data catalog config and instruments on mount (uses cache if available)
    fetchDataCatalogConfig()
    fetchBacktestInstruments()
  }, [fetchDataCatalogConfig, fetchBacktestInstruments])

  useEffect(() => {
    setLocalCatalogPath(dataCatalogPath)
  }, [dataCatalogPath])

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateStrategyParams(localParams)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (error) {
      console.error('Failed to save params:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setLocalParams(strategyParams)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="btn btn-outline"
            disabled={saving}
          >
            <RefreshCw size={16} className="mr-2" />
            Reset
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={saving}
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Strategy Selection */}
      <StrategySelector />

      {/* Data Catalog Settings */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-purple-400" />
            <h2 className="card-title">Data Catalog</h2>
          </div>
          <span className="text-xs text-[#666]">Backtest data source</span>
        </div>

        <div className="space-y-4">
          {/* Path Input */}
          <div>
            <label className="input-label">Catalog Path</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  className="input pr-10"
                  value={localCatalogPath}
                  onChange={(e) => setLocalCatalogPath(e.target.value)}
                  placeholder={dataCatalogDefaultPath || "/path/to/data/catalog"}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {dataCatalogLoading ? (
                    <Loader2 size={16} className="text-[#666] animate-spin" />
                  ) : dataCatalogExists ? (
                    <CheckCircle size={16} className="text-green-400" />
                  ) : (
                    <XCircle size={16} className="text-red-400" />
                  )}
                </div>
              </div>
              <button
                onClick={async () => {
                  const success = await setDataCatalogPath(localCatalogPath)
                  if (success) {
                    setCatalogPathSaved(true)
                    setTimeout(() => setCatalogPathSaved(false), 2000)
                  }
                }}
                className="btn btn-primary"
                disabled={dataCatalogLoading || localCatalogPath === dataCatalogPath}
              >
                <FolderOpen size={16} className="mr-2" />
                {catalogPathSaved ? 'Saved!' : 'Set Path'}
              </button>
              <button
                onClick={() => scanDataCatalog()}
                className="btn btn-outline"
                disabled={dataCatalogLoading || dataAuditRunning}
              >
                <RefreshCw size={16} className={dataCatalogLoading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => runDataAudit()}
                className="btn btn-outline"
                disabled={dataAuditRunning || dataCatalogLoading}
                title="Scan every bar series, reclassify, and regenerate utils/data_quality.py (~3 min)"
              >
                <ShieldCheck size={16} className="mr-2" />
                {dataAuditRunning ? 'Auditing...' : 'Run Audit'}
              </button>
            </div>
            <p className="text-xs text-[#666] mt-1">
              Path to the ParquetDataCatalog directory containing backtest data
            </p>

            {/* Audit Progress */}
            {dataAuditRunning && dataAuditProgress && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#888]">
                    {dataAuditProgress.step === 'scan'
                      ? `Scanning ${dataAuditProgress.current} of ${dataAuditProgress.total || '?'}`
                      : dataAuditProgress.step === 'report'
                        ? 'Classifying contracts...'
                        : 'Writing data_quality.py...'}
                  </span>
                  <span className="text-white font-mono text-xs">{dataAuditProgress.step}</span>
                </div>
                <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 transition-all duration-300"
                    style={{ width: `${dataAuditProgress.total ? Math.round((dataAuditProgress.current / dataAuditProgress.total) * 100) : 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Audit Summary */}
          {dataAuditSummary && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3">
              <CheckCircle size={16} className="text-green-400 mt-0.5" />
              <div>
                <p className="text-sm text-green-400 font-medium">Audit complete</p>
                <p className="text-xs text-[#666] mt-1">
                  {dataAuditSummary.usable} usable, {dataAuditSummary.caveat} caveat,{' '}
                  {dataAuditSummary.notSuitable} not suitable of {dataAuditSummary.total} contracts
                </p>
              </div>
            </div>
          )}

          {/* Audit Error */}
          {dataAuditError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <XCircle size={16} className="text-red-400 mt-0.5" />
              <div>
                <p className="text-sm text-red-400 font-medium">Audit failed</p>
                <p className="text-xs text-[#666] mt-1">{dataAuditError}</p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {dataCatalogError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <XCircle size={16} className="text-red-400 mt-0.5" />
              <div>
                <p className="text-sm text-red-400 font-medium">Error</p>
                <p className="text-xs text-[#666] mt-1">{dataCatalogError}</p>
              </div>
            </div>
          )}

          {/* Instruments Found */}
          {backtestInstruments.length > 0 && (
            <div>
              <label className="input-label">Available Instruments ({backtestInstruments.length})</label>
              <div className="bg-[#1a1a1a] rounded-lg max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#1a1a1a]">
                    <tr className="border-b border-[#333]">
                      <th className="text-left p-2 text-[#666] font-medium">Instrument</th>
                      <th className="text-left p-2 text-[#666] font-medium">Start Date</th>
                      <th className="text-left p-2 text-[#666] font-medium">End Date</th>
                      <th className="text-right p-2 text-[#666] font-medium">Bars</th>
                      <th className="text-left p-2 text-[#666] font-medium">Data Quality</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backtestInstruments.map((instrument) => (
                      <tr key={instrument.id} className="border-b border-[#222]">
                        <td className="p-2 text-white font-mono">{instrument.id}</td>
                        <td className="p-2 text-[#999]">{instrument.startDate || '-'}</td>
                        <td className="p-2 text-[#999]">{instrument.endDate || '-'}</td>
                        <td className="p-2 text-[#999] text-right">{instrument.barCount?.toLocaleString() || '-'}</td>
                        <td className="p-2">
                          {instrument.dataQuality
                            ? (instrument.dataQualityStatus === 'caveat'
                                ? <CaveatBadge reason={instrument.dataQuality} />
                                : <DataQualityBadge reason={instrument.dataQuality} />)
                            : <span className="text-[#444]">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No Instruments */}
          {!dataCatalogLoading && dataCatalogExists && backtestInstruments.length === 0 && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-3">
              <AlertTriangle size={16} className="text-yellow-400 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-400 font-medium">No instruments found</p>
                <p className="text-xs text-[#666] mt-1">
                  The catalog path exists but no instrument data was found. Verify the path contains valid ParquetDataCatalog files.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Strategy Parameters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <SettingsIcon size={16} className="text-blue-400" />
            <h2 className="card-title">Strategy Parameters</h2>
          </div>
          <span className="text-xs text-[#666]">Real-time adjustable</span>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Stop Loss */}
          <div>
            <label className="input-label">Stop Loss (%)</label>
            <input
              type="number"
              className="input"
              value={localParams.stop_loss_percent}
              onChange={(e) => setLocalParams({
                ...localParams,
                stop_loss_percent: parseFloat(e.target.value) || 0
              })}
              step="0.1"
              min="0.1"
              max="50"
            />
            <p className="text-xs text-[#666] mt-1">
              Maximum loss before position is closed
            </p>
          </div>

          {/* Take Profit */}
          <div>
            <label className="input-label">Take Profit (%)</label>
            <input
              type="number"
              className="input"
              value={localParams.take_profit_percent}
              onChange={(e) => setLocalParams({
                ...localParams,
                take_profit_percent: parseFloat(e.target.value) || 0
              })}
              step="0.1"
              min="0.1"
              max="100"
            />
            <p className="text-xs text-[#666] mt-1">
              Target profit for position closure
            </p>
          </div>

          {/* Trailing Stop */}
          <div>
            <label className="input-label">Trailing Stop (%)</label>
            <input
              type="number"
              className="input"
              value={localParams.trailing_stop_percent}
              onChange={(e) => setLocalParams({
                ...localParams,
                trailing_stop_percent: parseFloat(e.target.value) || 0
              })}
              step="0.1"
              min="0.1"
              max="50"
            />
            <p className="text-xs text-[#666] mt-1">
              Dynamic stop loss that follows price
            </p>
          </div>

          {/* Max Position Size */}
          <div>
            <label className="input-label">Max Position Size</label>
            <input
              type="number"
              className="input"
              value={localParams.max_position_size}
              onChange={(e) => setLocalParams({
                ...localParams,
                max_position_size: parseInt(e.target.value) || 1
              })}
              step="1"
              min="1"
              max="100"
            />
            <p className="text-xs text-[#666] mt-1">
              Maximum contracts per position
            </p>
          </div>

          {/* Min Volume A */}
          <div>
            <label className="input-label">Min Volume A</label>
            <input
              type="number"
              className="input"
              value={localParams.min_volume_a}
              onChange={(e) => setLocalParams({
                ...localParams,
                min_volume_a: parseInt(e.target.value) || 0
              })}
              step="1"
              min="0"
              max="100000"
            />
            <p className="text-xs text-[#666] mt-1">
              Minimum volume filter for strategy A
            </p>
          </div>

          {/* Min Volume B */}
          <div>
            <label className="input-label">Min Volume B</label>
            <input
              type="number"
              className="input"
              value={localParams.min_volume_b}
              onChange={(e) => setLocalParams({
                ...localParams,
                min_volume_b: parseInt(e.target.value) || 0
              })}
              step="1"
              min="0"
              max="100000"
            />
            <p className="text-xs text-[#666] mt-1">
              Minimum volume filter for strategy B
            </p>
          </div>
        </div>
      </div>

      {/* Indicator Settings */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Indicator Settings</h2>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div>
            <label className="input-label">EMA Fast Period</label>
            <input
              type="number"
              className="input"
              value={12}
              disabled
            />
          </div>
          <div>
            <label className="input-label">EMA Slow Period</label>
            <input
              type="number"
              className="input"
              value={26}
              disabled
            />
          </div>
          <div>
            <label className="input-label">MACD Signal Period</label>
            <input
              type="number"
              className="input"
              value={9}
              disabled
            />
          </div>
        </div>

        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-3">
          <AlertTriangle size={16} className="text-yellow-400 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-400 font-medium">Indicator settings require restart</p>
            <p className="text-xs text-[#666] mt-1">
              Changes to indicator periods will only take effect after restarting the strategy
            </p>
          </div>
        </div>
      </div>

      {/* Connection Settings */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Connection Settings</h2>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="input-label">API Server</label>
            <input
              type="text"
              className="input"
              value={API_BASE}
              disabled
            />
          </div>
          <div>
            <label className="input-label">WebSocket</label>
            <input
              type="text"
              className="input"
              value={`ws://${window.location.host}/api/ws`}
              disabled
            />
          </div>
        </div>
      </div>

      {/* Risk Management */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Risk Management</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
            <div>
              <p className="text-sm font-medium text-white">Max Daily Loss</p>
              <p className="text-xs text-[#666]">Stop trading when daily loss exceeds limit</p>
            </div>
            <input
              type="number"
              className="input w-32 text-right"
              value={1000}
              disabled
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
            <div>
              <p className="text-sm font-medium text-white">Max Drawdown (%)</p>
              <p className="text-xs text-[#666]">Emergency stop at maximum drawdown</p>
            </div>
            <input
              type="number"
              className="input w-32 text-right"
              value={10}
              disabled
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
            <div>
              <p className="text-sm font-medium text-white">Trading Hours</p>
              <p className="text-xs text-[#666]">Active trading window (UTC)</p>
            </div>
            <span className="text-sm text-white">09:00 - 16:00</span>
          </div>
        </div>
      </div>
    </div>
  )
}
